/**
 * The wide rail's app groups — ADR 9023 (bata-ui#196 §1), drawn as the designer's brief part 1
 * §3 draws them (Package C4, DEC-84, DEC-91, DEC-95).
 *
 * Under an "App" kicker, the apps in `RAIL_APPS` order, each a disclosure button
 * (`aria-expanded`, `aria-controls`). ONE app is open at a time (`useRailOpenGroups`), and
 * opening one never navigates: the page changes only on an entry. Enter and Space toggle a
 * heading because it is a button; Up and Down walk every heading and every visible entry
 * (`moveRailFocus`, bound on the `<nav>` by the chrome); the rail's own focus ring is drawn on
 * all of them.
 *
 * - **A closed app**: 14px in `rail-fg-entry`, its 18px icon, a quiet "›" at the end.
 * - **The open app**: the same heading in white 700 with a white "⌄", and NO red bar and no
 *   other label (R-06: the white heading and the page's breadcrumb already say where you are).
 * - **Its entries** hang under the heading's icon on a 1px rule: 13px, 32px tall. An entry with
 *   sub-entries (ADR 9023's amendment of 2026-09-30) draws them one step further in, on the same
 *   rule, while the planner is on it or on one of them.
 * - **The entry the planner is on**: white 700 on `rail-active`, and a 3px brand-red bar ON the
 *   rule — the only red in the rail. When the planner closes the app holding the page, the bar
 *   moves to that app's heading, so "where am I" never disappears into a closed dropdown.
 * - **Future expansions** (DEC-91; a group like the apps since the owner's request of
 *   2026-10-12): a disclosure heading of its own — the same `AppHeading`, chevron, `aria-expanded`
 *   and `aria-controls` — CLOSED until the planner opens it (or arrives by deep link into one of
 *   its screens), one group open at a time with the apps and remembered with them. Its screens
 *   hang on the same rule: `rail-fg-future`, 13px, no icon, no hover, `aria-disabled`,
 *   `cursor-not-allowed`, "Not yet available" as the tooltip. Focusable, so the tooltip and the
 *   reason reach a keyboard, and inert. The heading is a control, so it takes the apps' ink.
 */

import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { RAIL_GROUP_ICONS } from './rail-icons';
import { RAIL_ITEM } from './rail-groups';
import {
  FUTURE_GROUP, isFutureHere,
  type ActiveRailLink, type RailGroupId, type RailLayout, type RailLink,
} from './rail-layout';

export interface RailAppsProps {
  readonly layout: RailLayout;
  readonly active: ActiveRailLink | undefined;
  readonly currentPath: string;
  /** The chapter the path is in, for a future chapter reached by deep link. */
  readonly activeSlug: string | undefined;
  /** The open app — a set of at most one (`useRailOpenGroups`). */
  readonly open: ReadonlySet<string>;
  readonly onToggle: (id: RailGroupId) => void;
  readonly onOpenLink: (link: RailLink) => void;
  /** The rail's focus ring, shared with the rest of the chrome. */
  readonly focus: string;
}

const panelId = (id: string): string => `rail-group-${id}`;

/** A section kicker ("App", "Future expansions"), under a rule. */
const KICKER = 'border-rail-hairline mx-[20px] mb-1 mt-[10px] border-t pt-[10px]';

function AppHeading({ id, labelKey, isOpen, marked, onToggle, focus }: {
  readonly id: RailGroupId; readonly labelKey: string; readonly isOpen: boolean;
  readonly marked: boolean; readonly onToggle: (id: RailGroupId) => void; readonly focus: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const Icon = RAIL_GROUP_ICONS[id];
  return (
    <div className={`relative flex items-center ${marked ? 'bg-rail-active' : 'hover:bg-rail-hover'}`}>
      {/* §5.2 rule 1, the active navigation indicator — here only while the app holding the
          page is closed; open, the bar is on the entry. */}
      {marked && <span aria-hidden className="bg-brand-red absolute inset-y-1 start-0 w-[3px]" />}
      <button
        type="button"
        {...RAIL_ITEM}
        aria-expanded={isOpen}
        aria-controls={panelId(id)}
        onClick={() => onToggle(id)}
        className={[
          'text-body flex min-h-[36px] min-w-0 grow items-center gap-3 py-1 pe-4 ps-[20px] text-start',
          isOpen || marked ? 'text-rail-fg font-bold' : 'text-rail-fg-entry hover:text-rail-fg',
          focus,
        ].join(' ')}
      >
        <Icon size={18} aria-hidden className="shrink-0" />
        {/* One line, so a long label (Spanish runs longest) is cut — and the whole of it is the
            entry's `title` (usability audit D2: no rail entry had one). */}
        <span className="grow truncate" title={t(labelKey)}>{t(labelKey)}</span>
        {isOpen
          ? <ChevronDown size={14} aria-hidden className="text-rail-fg shrink-0" />
          : <ChevronRight size={14} aria-hidden className="text-rail-fg-quiet shrink-0 rtl:-scale-x-100" />}
      </button>
    </div>
  );
}

/**
 * One entry of an open app, on the app's rule: a top-level entry, or — `nested` — a sub-entry
 * one step further in. `inside` sets a parent in white while one of its sub-entries is where
 * the planner is, without the bar.
 */
function EntryRow({ link, here, inside = false, nested, onOpenLink, focus }: {
  readonly link: RailLink; readonly here: boolean; readonly inside?: boolean; readonly nested: boolean;
  readonly onOpenLink: (link: RailLink) => void; readonly focus: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={`relative flex items-center ${here ? 'bg-rail-active' : 'hover:bg-rail-hover'}`}>
      {/* The one red in the rail: 3px, on the rule (which sits 1px before this row), 6px in
          from the row's top and bottom. */}
      {here && <span aria-hidden className="bg-brand-red absolute inset-y-[6px] start-[-2px] w-[3px]" />}
      <button
        type="button"
        {...RAIL_ITEM}
        onClick={() => onOpenLink(link)}
        aria-current={here ? 'page' : undefined}
        className={[
          'flex min-h-8 min-w-0 grow items-center py-1 pe-4 text-start text-[13px] leading-4',
          nested ? 'ps-8' : 'ps-[20px]',
          here ? 'text-rail-fg font-bold' : inside ? 'text-rail-fg font-semibold' : 'text-rail-fg-entry hover:text-rail-fg',
          focus,
        ].join(' ')}
      >
        <span className="grow truncate" title={t(link.entry.labelKey)}>{t(link.entry.labelKey)}</span>
      </button>
    </div>
  );
}

export function RailApps({
  layout, active, currentPath, activeSlug, open, onToggle, onOpenLink, focus,
}: RailAppsProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      {layout.apps.length > 0 && (
        <span className={`${KICKER} text-rail-fg-secondary text-[10px] uppercase leading-4 tracking-[0.14em]`}>
          {t('nav.appsSection')}
        </span>
      )}
      {layout.apps.map((app) => {
        const isOpen = open.has(app.id);
        return (
          <div key={app.id}>
            <AppHeading
              id={app.id} labelKey={app.labelKey} isOpen={isOpen}
              marked={!isOpen && active?.app === app.id} onToggle={onToggle} focus={focus}
            />
            {isOpen && (
              <div
                id={panelId(app.id)} role="group" aria-label={t(app.labelKey)}
                // Under the heading's icon (20px padding + half of 18px = 29px), on a 1px rule.
                className="border-rail-hairline ms-[29px] flex flex-col border-s"
              >
                {app.links.map((link) => {
                  const inLink = link.key === active?.link.key;
                  // The app's common entries — Parametri · Approvazioni in attesa · Documentazione —
                  // stand after a `#3a2b2b` rule (brief part 2 §1, DEC-89).
                  const rule = link.common === true
                    ? <span aria-hidden="true" data-rail-common-rule="" className="border-rail-hairline my-1 me-4 block border-t" />
                    : null;
                  if (link.children.length === 0) {
                    return rule === null
                      ? <EntryRow key={link.key} link={link} here={inLink} nested={false} onOpenLink={onOpenLink} focus={focus} />
                      : (
                        <div key={link.key} className="flex flex-col">
                          {rule}
                          <EntryRow link={link} here={inLink} nested={false} onOpenLink={onOpenLink} focus={focus} />
                        </div>
                      );
                  }
                  /*
                    AN ENTRY WITH SUB-ENTRIES — ADR 9023's amendment of 2026-09-30 (DEC-84).
                    Expanded while the planner is on it or on one of its sub-entries, so the
                    sub-entries are rendered, and therefore in the tab and arrow-key order,
                    exactly then. The heading opens its first view; the brand red goes to the
                    view the planner is on, and the heading is set in white meanwhile, so the
                    pair reads as "here" without spending the indicator twice.
                  */
                  return (
                    <div key={link.key}>
                      {rule}
                      <EntryRow link={link} here={inLink && active?.child === undefined} inside={inLink} nested={false} onOpenLink={onOpenLink} focus={focus} />
                      {inLink && (
                        <div role="group" aria-label={t(link.entry.labelKey)}>
                          {link.children.map((child) => (
                            <EntryRow
                              key={child.key} link={child} here={child.key === active?.child?.key}
                              nested onOpenLink={onOpenLink} focus={focus}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {layout.future.length > 0 && (() => {
        const futureOpen = open.has(FUTURE_GROUP);
        // Arrived by deep link into a future screen: the group is open by itself
        // (`useRailOpenGroups`); closed again, its heading carries the indicator.
        const inFuture = active === undefined && layout.future.some((item) => isFutureHere(item, currentPath, activeSlug));
        return (
          <div>
            <span aria-hidden className="border-rail-hairline mx-[20px] mb-1 mt-[10px] block border-t" />
            <AppHeading
              id={FUTURE_GROUP} labelKey="nav.futureExpansions" isOpen={futureOpen}
              marked={!futureOpen && inFuture} onToggle={onToggle} focus={focus}
            />
            {futureOpen && (
              <div
                id={panelId(FUTURE_GROUP)} role="group" aria-label={t('nav.futureExpansions')}
                className="border-rail-hairline ms-[29px] flex flex-col border-s"
              >
                {layout.future.map((item) => {
                  const { chapter } = item;
                  const key = item.kind === 'chapter' ? chapter.slug : item.entry.to;
                  // Arrived by deep link: this is still where the planner is, so it carries the
                  // indicator even though the rail offers no way in.
                  const here = inFuture && isFutureHere(item, currentPath, activeSlug);
                  const futureLabel = item.kind === 'chapter'
                    ? t(chapter.navLabelKey)
                    : t('nav.futureScreen', { chapter: t(chapter.navLabelKey), screen: t(item.entry.labelKey) });
                  return (
                    <div key={key} className={`relative flex items-center ${here ? 'bg-rail-active' : ''}`}>
                      {here && <span aria-hidden className="bg-brand-red absolute inset-y-[6px] start-[-2px] w-[3px]" />}
                      {/* Inert, not absent (DEC-91): focusable (§12.8), `aria-disabled`, and the reason
                          as the tooltip — no hover, no icon, and no opacity (#19). */}
                      <button
                        type="button"
                        {...RAIL_ITEM}
                        aria-disabled
                        aria-current={here ? 'page' : undefined}
                        // The label AND the reason (usability audit D2): the label is cut on one line,
                        // and a tooltip that gave only the reason left the cut name unreadable.
                        title={t('nav.futureEntryTitle', { label: futureLabel })}
                        className={[
                          'text-rail-fg-future flex min-h-8 min-w-0 grow cursor-not-allowed items-center py-1 pe-4 ps-[20px] text-start text-[13px] leading-4',
                          here ? 'font-semibold' : '',
                          focus,
                        ].join(' ')}
                      >
                        <span className="grow truncate">{futureLabel}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}
    </>
  );
}
